import React from 'react';
import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleAuthModal } from '../../redux/authentication/action';
import { useAuth } from '../../module/AuthenticationModule';
import { useMergedCreatorFollowState } from '../../hook/useMergedCreatorFollowState';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { followAThspian, unfollowThspian } from '../../redux/audience/action';
import { APP_ROUTES } from '../../types/global/routes.types';
import { getIdForIbjects, getJWtDetails } from '../../utils/AppUtils';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { getUgcCampaignMaxSlots } from '../../utils/ugcCampaignCapacity';
import { CampaignFillingFastBadge } from '../BuzzBridge/CampaignFillingFastBadge';
import { isPublicCampaignFillingFast } from '../../utils/campaignParticipationGate';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';

const MAX_NAME_LENGTH = 16;

function truncate(str: string, max: number): string {
    return str.length > max ? `${str.slice(0, max)}…` : str;
}

/** e.g. "Victor O." — ellipsis handled in layout via CSS when space is tight */
function formatCreatorShortName(creator: { firstName?: string; lastName?: string; userName?: string }): string {
    const first = (creator.firstName || '').trim();
    const last = (creator.lastName || '').trim();
    const user = (creator.userName || '').trim();
    if (first && last) {
        return `${first} ${last[0].toUpperCase()}.`;
    }
    if (first) return first;
    if (user) return user;
    return 'Creator';
}

function postedAgo(dateStr?: string): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `Posted ${mins} mins ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Posted ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Posted ${days}d ago`;
}

export interface IUGCCampaign {
    _id: string;
    title: string;
    description?: string;
    brand?: { _id: string; name: string };
    /** Active roster count from API — not max slots */
    participants_count?: number;
    /** Max creator slots (host-configured) */
    total_participants_count?: number;
    reward?: string;
    influencer_tier?: string;
    platforms?: string[];
    total_budget?: number;
    funded?: boolean;
    approved?: boolean;
    status?: string;
    start_date?: string;
    end_date?: string;
    feature_image?: string;
    createdAt?: string;
    created_by?: any;
    filling_fast?: boolean;
    at_capacity?: boolean;
}

interface Props {
    campaign: IUGCCampaign;
    onClick?: (id: string) => void;
}

const UGCCampaignCard: React.FC<Props> = ({ campaign, onClick }) => {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const navigateToDetail = (source: 'card' | 'join_cta' = 'card') => {
        if (source === 'join_cta') {
            MixPanel.track(MIXPANEL_EVENT.JOIN_CAMPAIGN_CLICK, {
                buttonName: 'JOIN CAMPAIGN',
                buttonAction: 'JOIN_CAMPAIGN',
                campaignId: campaign._id,
                campaignTitle: campaign.title,
                source: 'ugc_campaign_card',
            });
        }
        const route = replaceRouteParam(APP_ROUTES.UGC_CAMPAIGN_DETAIL, 'campaignId', campaign._id);
        navigate(route);
    };

    const creator = typeof campaign.created_by === 'object' && campaign.created_by ? campaign.created_by : null;
    const displayName = creator
        ? formatCreatorShortName(creator)
        : truncate(campaign.brand?.name || 'Brand', MAX_NAME_LENGTH);
    const avatarSrc = creator?.profile_picture;
    const hasCreator = !!creator;
    const creatorId = hasCreator ? creator._id : '';
    const { user_id: currentUserId } = getJWtDetails();
    const resolvedCreatorId = hasCreator ? String(getIdForIbjects(creator._id)) : '';
    const isOwnCampaign = Boolean(currentUserId && resolvedCreatorId && resolvedCreatorId === String(currentUserId));

    const { isFollowing, followDocId, followingInProgress, followMatch } = useMergedCreatorFollowState(
        creator,
        creatorId,
    );
    const { isSignedIn } = useAuth();

    const posted = postedAgo(campaign.createdAt);
    const dateLabel = campaign.start_date
        ? new Date(campaign.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : '';
    const endLabel = campaign.end_date
        ? new Date(campaign.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';

    const rewardLabel =
        campaign.reward === 'paid'
            ? 'Paid'
            : campaign.reward === 'gifted'
            ? 'Gifted'
            : campaign.reward === 'both'
            ? 'Paid + Gifted'
            : '';

    const creatorsNeeded = getUgcCampaignMaxSlots(campaign);
    const fillingFast = isPublicCampaignFillingFast(campaign);

    const handleFollow = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!creatorId) return;
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }
        if (isFollowing && followDocId) {
            dispatch(
                unfollowThspian({
                    documentId: followDocId,
                    additionalPayload: (followMatch ?? creator?.user_following?.[0]) as any,
                }),
            );
        } else if (!isFollowing) {
            dispatch(followAThspian({ following: creatorId, from: 'discovery' }));
        }
    };

    return (
        <Box
            onClick={() => navigateToDetail('card')}
            sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                alignSelf: 'stretch',
                boxSizing: 'border-box',
                borderRadius: '20px',
                bgcolor: '#131517',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: '16px', sm: '20px 24px' },
                gap: { xs: '14px', sm: '20px' },
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.15s, box-shadow 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    maxWidth: '100vw',
                    minWidth: 0,
                }}>
                <Box
                    sx={{
                        borderRadius: 999,
                        bgcolor: 'rgba(251,188,85,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        p: '3px',
                        pl: '3px',
                        pr: hasCreator && !isOwnCampaign ? '3px' : 1.5,
                        flex: '0 1 auto',
                        minWidth: 0,
                        maxWidth: '50%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                    onClick={e => e.stopPropagation()}>
                    <Avatar src={avatarSrc} sx={{ width: 32, height: 32, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography
                            noWrap
                            title={displayName}
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                                color: '#fff',
                                lineHeight: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {displayName}
                        </Typography>
                    </Box>
                    {hasCreator && !isOwnCampaign && (
                        <Button
                            size="small"
                            disabled={followingInProgress || (isFollowing && !followDocId)}
                            onClick={handleFollow}
                            sx={{
                                minWidth: 0,
                                flexShrink: 0,
                                borderRadius: 999,
                                bgcolor: isFollowing ? '#d32f2f' : t.primary_orange,
                                color: isFollowing ? '#fff' : '#131517',
                                px: 1.5,
                                py: 0.5,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                textTransform: 'none',
                                mr: 0.5,
                                lineHeight: '16px',
                                '&:hover': { bgcolor: isFollowing ? '#b71c1c' : t.primary_orange },
                            }}
                            startIcon={
                                isFollowing ? (
                                    <PersonRemoveOutlinedIcon sx={{ fontSize: '14px !important' }} />
                                ) : (
                                    <AddIcon sx={{ fontSize: '14px !important' }} />
                                )
                            }>
                            {isFollowing ? 'Unfollow' : 'Follow'}
                        </Button>
                    )}
                </Box>

                {posted && (
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: '#9ea5ad' }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 10,
                                color: '#9ea5ad',
                                fontStyle: 'italic',
                                whiteSpace: 'nowrap',
                            }}>
                            {posted}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Feature image */}
            {campaign.feature_image && (
                <Box
                    sx={{
                        position: 'relative',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        height: { xs: 180, sm: 200 },
                    }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            display: 'block',
                            lineHeight: 0,
                            '& .lazy-load-image-background, & .lazy-load-image-wrapper, & > span': {
                                display: 'block !important',
                                maxWidth: '100% !important',
                                width: '100% !important',
                                height: '100% !important',
                            },
                            '& img': {
                                display: 'block',
                                maxWidth: '100%',
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            },
                        }}>
                        <CustomLazyLoadImage
                            src={campaign.feature_image}
                            alt={campaign.title}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                maxWidth: '100%',
                                display: 'block',
                            }}
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            right: 12,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {rewardLabel && (
                                <Chip
                                    label={rewardLabel}
                                    size="small"
                                    sx={{
                                        bgcolor: '#24292e',
                                        color: '#fff',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 10,
                                        height: 24,
                                    }}
                                />
                            )}
                            {campaign.influencer_tier && (
                                <Chip
                                    label={campaign.influencer_tier}
                                    size="small"
                                    sx={{
                                        bgcolor: '#24292e',
                                        color: '#ffc965',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 10,
                                        height: 24,
                                        textTransform: 'capitalize',
                                    }}
                                />
                            )}
                        </Box>
                        <BookmarkBorderOutlinedIcon
                            sx={{
                                fontSize: 22,
                                color: '#fff',
                                opacity: 0.7,
                                cursor: 'pointer',
                                '&:hover': { opacity: 1 },
                            }}
                        />
                    </Box>
                    {fillingFast && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 12,
                                bottom: 12,
                                zIndex: 1,
                            }}>
                            <CampaignFillingFastBadge />
                        </Box>
                    )}
                </Box>
            )}

            {/* Info section */}
            <Box
                sx={{
                    bgcolor: 'rgba(46,49,52,0.3)',
                    borderRadius: '4px',
                    p: 1,
                    minWidth: 0,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 15,
                        color: '#fff',
                        lineHeight: '22px',
                        px: 1,
                        py: 1,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}>
                    {campaign.title}
                </Typography>
                {dateLabel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, minWidth: 0 }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#ced2d6', flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: '#ced2d6',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {dateLabel}
                            {endLabel ? ` – ${endLabel}` : ''}
                        </Typography>
                    </Box>
                )}
                {campaign.total_budget != null && campaign.total_budget > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, minWidth: 0 }}>
                        <PaidOutlinedIcon sx={{ fontSize: 16, color: '#ced2d6', flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: '#ced2d6',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            ${campaign.total_budget.toLocaleString()} budget
                        </Typography>
                    </Box>
                )}
                {creatorsNeeded != null && creatorsNeeded > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, minWidth: 0 }}>
                        <GroupOutlinedIcon sx={{ fontSize: 18, color: '#ced2d6', flexShrink: 0 }} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: '#ced2d6',
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {creatorsNeeded} creator{creatorsNeeded > 1 ? 's' : ''} needed
                        </Typography>
                    </Box>
                )}
                {campaign.platforms && campaign.platforms.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, flexWrap: 'wrap' }}>
                        {campaign.platforms.map(p => (
                            <Chip
                                key={p}
                                label={p}
                                size="small"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                    height: 22,
                                    bgcolor: 'rgba(255,255,255,0.06)',
                                    color: '#ced2d6',
                                    textTransform: 'capitalize',
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>

            {/* CTA — outer box clips any sub-pixel / flex overflow from MUI Button on narrow widths */}
            <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
                <Button
                    fullWidth
                    onClick={e => {
                        e.stopPropagation();
                        navigateToDetail('join_cta');
                    }}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                    sx={{
                        borderRadius: 999,
                        bgcolor: t.primary_orange,
                        border: `1px solid ${t.primary_orange}`,
                        color: '#131517',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 14,
                        textTransform: 'none',
                        py: 1.2,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        flexShrink: 0,
                        '&:hover': { bgcolor: t.primary_orange },
                    }}>
                    Join campaign
                </Button>
            </Box>
        </Box>
    );
};

export { UGCCampaignCard };
